import { ref, computed, onMounted, onUnmounted, provide, inject } from 'vue'

const SidebarSymbol = Symbol()

export function useSidebarProvider() {
  const isExpanded = ref(true)
  const isMobileOpen = ref(false)
  const isMobile = ref(false)
  const isHovered = ref(false)
  const activeItem = ref(null)
  /**
   * WHICH accordions are open — a Set, not a single key, since 2026-09-04 (§14.5 N3).
   *
   * It held one string, so opening B closed A. «داوری و ترمیم» and «سنجش سلامت بانک» are argued in
   * `components/layout/navigation.js` as two halves of one job, and the single-key model made them
   * mutually exclusive: moving between them cost two clicks and destroyed the list you had just been
   * reading. Keyed by the accordion's stable id, because positions shift with permissions.
   *
   * Session-only, deliberately. `AppSidebar.vue` owns the semantics; this ref is only where the state
   * lives so it survives the sidebar being re-rendered.
   */
  const openSubmenu = ref(new Set())

  const handleResize = () => {
    const mobile = window.innerWidth < 768
    isMobile.value = mobile
    if (!mobile) {
      isMobileOpen.value = false
    }
  }

  onMounted(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
  })

  const toggleSidebar = () => {
    if (isMobile.value) {
      isMobileOpen.value = !isMobileOpen.value
    } else {
      isExpanded.value = !isExpanded.value
    }
  }

  const toggleMobileSidebar = () => {
    isMobileOpen.value = !isMobileOpen.value
  }

  const setIsHovered = (value) => {
    isHovered.value = value
  }

  const setActiveItem = (item) => {
    activeItem.value = item
  }

  /**
   * Kept in the context because it has always been exported, and rewritten for the Set so it cannot
   * silently destroy the open state. `AppSidebar.vue` does NOT call this — it owns the interaction,
   * including the «an accordion holding the current page reopens itself» case this helper knows
   * nothing about — so nothing in the app reaches it today (`rg toggleSubmenu` → the sidebar's own
   * local function and this one). Delete it, with the `rg` to prove no importer, or leave it correct.
   */
  const toggleSubmenu = (item) => {
    const next = new Set(openSubmenu.value)
    if (!next.delete(item)) next.add(item)
    openSubmenu.value = next
  }

  const context = {
    isExpanded: computed(() => (isMobile.value ? false : isExpanded.value)),
    isMobileOpen,
    isHovered,
    activeItem,
    openSubmenu,
    toggleSidebar,
    toggleMobileSidebar,
    setIsHovered,
    setActiveItem,
    toggleSubmenu,
  }

  provide(SidebarSymbol, context)

  return context
}

export function useSidebar() {
  const context = inject(SidebarSymbol)
  if (!context) {
    throw new Error(
      'useSidebar must be used within a component that has SidebarProvider as an ancestor',
    )
  }
  return context
}
